import React, { useEffect, useState, useCallback, useRef } from 'react'
import { host } from '../script/variables'
import { Link } from 'react-router-dom'
import InfiniteScroll from 'react-infinite-scroll-component'
import '../style/useraction.css'

function timeAgo(ts) {
  const diff = Date.now() - new Date(ts)
  const s = Math.floor(diff / 1000), m = Math.floor(s / 60), h = Math.floor(m / 60)
  const d = Math.floor(h / 24), w = Math.floor(d / 7), mo = Math.floor(d / 30)
  if (mo > 0) return `${mo}mo ago`
  if (w  > 0) return `${w}w ago`
  if (d  > 0) return `${d}d ago`
  if (h  > 0) return `${h}h ago`
  if (m  > 0) return `${m}m ago`
  return s <= 5 ? 'just now' : `${s}s ago`
}

const VIS_OPTIONS = [
  { value: 'public',    label: 'Public',    dotClass: 'public'    },
  { value: 'private',   label: 'Private',   dotClass: 'private'   },
  { value: 'protected', label: 'Protected', dotClass: 'protected' },
]

/* ── Small dropdown component ── */
const VisDropdown = ({ current, onSelect, label }) => {
  const [open, setOpen] = useState(false)
  const ref = useRef(null)

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false) }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  const cur = VIS_OPTIONS.find(o => o.value === current) || VIS_OPTIONS[0]

  return (
    <div className="act-item-vis" ref={ref}>
      <button
        className={`act-vis-badge ${cur.dotClass}`}
        onClick={() => setOpen(o => !o)}
      >
        <span className={`act-vis-dot ${cur.dotClass}`} />
        {cur.label}
      </button>
      {open && (
        <div className="act-item-menu">
          {VIS_OPTIONS.map(opt => (
            <div
              key={opt.value}
              className="act-vis-menu-item"
              onClick={() => { onSelect(opt.value); setOpen(false) }}
            >
              <span className={`act-vis-dot ${opt.dotClass}`} />
              {opt.label}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

/* ── Bulk dropdown ── */
const BulkVisDropdown = ({ onSelect }) => {
  const [open, setOpen] = useState(false)
  const ref = useRef(null)

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false) }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  return (
    <div className="act-vis-dropdown" ref={ref}>
      <button className="act-vis-trigger" onClick={() => setOpen(o => !o)}>
        <svg viewBox="0 0 14 14" fill="none"><path d="M2 4h10M4 7h6M6 10h2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>
        Set all visibility
        <svg viewBox="0 0 10 6" fill="none" style={{ width: 10, height: 6, marginLeft: 2 }}>
          <path d="M1 1l4 4 4-4" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
        </svg>
      </button>
      {open && (
        <div className="act-vis-menu">
          {VIS_OPTIONS.map(opt => (
            <div
              key={opt.value}
              className="act-vis-menu-item"
              onClick={() => { onSelect(opt.value); setOpen(false) }}
            >
              <span className={`act-vis-dot ${opt.dotClass}`} />
              {opt.label}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

/* ═══════════════════════════════ */

const Actions = ({ isMyProfile, id }) => {
  const [accountType, setAccountType] = useState(null)
  const [actions, setActions]         = useState([])
  const [pageNo, setPageNo]           = useState(1)
  const [hasMore, setHasMore]         = useState(true)
  const [loading, setLoading]         = useState(false)

  const token = () => localStorage.getItem('token')

  const fetchActions = useCallback(async (pno) => {
    if (loading) return
    setLoading(true)
    try {
      const res = await fetch(`${host}/user/action`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: token() },
        body: JSON.stringify({ pno, email: id }),
      })
      if (res.ok) {
        const data = await res.json()
        if (data.length < 10) setHasMore(false)
        setActions(prev => pno === 1 ? data : [...prev, ...data])
        setPageNo(pno + 1)
      }
    } catch {}
    finally { setLoading(false) }
  }, [id, loading])

  const reload = () => {
    setActions([])
    setHasMore(true)
    setPageNo(1)
    fetchActions(1)
  }

  const changeVisibility = async (vis, actionId) => {
    try {
      const res = await fetch(`${host}/user/changeactionvis?vis=${vis}&aid=${actionId}`, {
        headers: { 'Content-Type': 'application/json', Authorization: token() },
      })
      if (res.ok) {
        setActions(prev => prev.map(a => a._id === actionId ? { ...a, visibility: vis } : a))
      }
    } catch {}
  }

  const changeVisibilityAll = async (vis) => {
    try {
      const res = await fetch(`${host}/user/changeactionvisall?vis=${vis}`, {
        headers: { 'Content-Type': 'application/json', Authorization: token() },
      })
      if (res.ok) reload()
    } catch {}
  }

  const fetchAccountType = async () => {
    try {
      const res = await fetch(`${host}/user/accounttype`, {
        headers: { 'Content-Type': 'application/json', Authorization: token() },
      })
      if (res.ok) {
        const data = await res.json()
        setAccountType(data.accounttype)
        if (data.accounttype === 'public') fetchActions(1)
      }
    } catch {}
  }

  useEffect(() => { fetchAccountType() }, [])

  if (accountType === null) {
    return <div className="act-loader"><div className="act-spinner" /></div>
  }

  if (accountType !== 'public') {
    return (
      <div className="act-private">
        <div className="act-private-icon">🔒</div>
        <p>Activity is private</p>
      </div>
    )
  }

  return (
    <div className="act-root">

      {/* Toolbar */}
      {isMyProfile && (
        <div className="act-toolbar">
          <span className="act-toolbar-label">
            {actions.length} action{actions.length !== 1 ? 's' : ''}
          </span>
          <BulkVisDropdown onSelect={changeVisibilityAll} />
        </div>
      )}

      {/* Feed */}
      <div id="act-scroll-target" style={{ height: 480, overflow: 'auto' }}>
        <InfiniteScroll
          dataLength={actions.length}
          next={() => fetchActions(pageNo)}
          hasMore={hasMore}
          scrollableTarget="act-scroll-target"
          loader={<div className="act-loader"><div className="act-spinner" /></div>}
          endMessage={<p className="act-end">✦ All caught up ✦</p>}
        >
          {actions.map((action, idx) => (
            <div
              className="act-item"
              key={action._id}
              style={{ animationDelay: `${Math.min(idx, 10) * 40}ms` }}
            >
              <div className="act-dot-col">
                <div className="act-dot" />
              </div>

              <div className="act-body">
                <p className="act-text">
                  <Link to={action.url}>
                    {action.action} a {action.content}
                  </Link>
                </p>
                <div className="act-footer">
                  <span className="act-time">{timeAgo(action.createdAt)}</span>
                  {isMyProfile && (
                    <VisDropdown
                      current={action.visibility}
                      onSelect={(vis) => changeVisibility(vis, action._id)}
                    />
                  )}
                </div>
              </div>
            </div>
          ))}

          {actions.length === 0 && !loading && (
            <div className="act-private">
              <div className="act-private-icon">📭</div>
              <p>No activity yet</p>
            </div>
          )}
        </InfiniteScroll>
      </div>
    </div>
  )
}

export default Actions